// src/utils/workPerformedEntries.js
// wpauthor-5d3f8a26 — WORK PERFORMED ATTRIBUTION, SEAM A.
//
// THE TWIN of functions/utils/workPerformedEntries.js. Read that file's header
// for the design; this is the same logic as an ES module for the web app.
//
// TWO COPIES EXIST ON PURPOSE: functions/ is a separate npm package and cannot
// import from ../src — the same reason appendToWorkPerformed is already
// duplicated between src/utils/promoteNote.js and completeJob. KEEP THEM
// IDENTICAL. If the flatten drifts between the two, the string the customer
// reads changes depending on whether a tech completed the job or an office user
// saved it, and nothing would report the difference.
//
// Seam A ships this file unused by any screen. Seam B (the web entry list) is
// its first caller. It is here now so the two copies land in the same commit and
// are reviewed side by side.
//
// PURE — no Firestore, no I/O.

export const WP_MARKER = 'wpauthor-5d3f8a26';

export const WP_ENTRIES_COLLECTION = 'workPerformedEntries';

// Shown where an author name would go on a block written before this seam.
// Never a person: a legacy block has no author and must never acquire one.
export const WP_NO_AUTHOR_LABEL = 'No author on file';

// Mirrored EXACTLY by the `allow update` clause in firestore.rules.
export const WP_IMMUTABLE_FIELDS = ['authorId', 'authorName', 'createdAt', 'legacy'];

// A just-written entry's serverTimestamp() reads back null on the local snapshot,
// so null sorts LAST — the only way to see one is to have just appended it.
export const toMillis = (v) => {
  if (!v) return null;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.seconds === 'number') return v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6);
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number') return v;
  return null;
};

export const compareEntries = (a, b) => {
  const am = toMillis(a && a.createdAt);
  const bm = toMillis(b && b.createdAt);
  if (am === null && bm === null) return String(a.id || '').localeCompare(String(b.id || ''));
  if (am === null) return 1;
  if (bm === null) return -1;
  if (am !== bm) return am - bm;
  return String(a.id || '').localeCompare(String(b.id || ''));
};

// Removal HIDES, never erases — the entry and its author stay visible internally.
export const isActiveEntry = (e) => !!e && e.removed !== true && typeof e.text === 'string' && e.text.trim() !== '';

export const sortWorkPerformedEntries = (entries) =>
  (Array.isArray(entries) ? [...entries] : []).sort(compareEntries);

// THE one place a list of entries becomes the string a customer reads.
//
// BYTE-IDENTICAL to appendToWorkPerformed applied left-to-right. That function
// right-trims the ACCUMULATED text but fully trims each INCOMING block, so
// folded from an empty string every block ends up FULLY trimmed. Right-trimming
// only diverges on any block starting with whitespace — i.e. most of them.
export const flattenWorkPerformedEntries = (entries) => {
  const active = sortWorkPerformedEntries(entries).filter(isActiveEntry);
  if (active.length === 0) return '';
  return active
    .map((e) => e.text.trim())
    .filter((t) => t !== '')
    .join('\n\n');
};

// A job whose workPerformed predates this seam has text but no entries, and
// nobody knows who wrote it. An edit stamps editedBy but never backfills
// authorId — the honest answer is "no author on file", forever.
export const buildLegacyWorkPerformedEntry = (text, createdAt) => ({
  text: typeof text === 'string' ? text : '',
  authorId: null,
  authorName: '',
  createdAt: createdAt || null,
  editedAt: null,
  editedById: null,
  editedByName: null,
  removed: false,
  removedAt: null,
  removedById: null,
  removedByName: null,
  sourceNoteId: null,
  legacy: true,
});

// authorId MUST be the writer's own uid — the create rule refuses anything else.
export const buildWorkPerformedEntry = ({ text, authorId, authorName, createdAt, sourceNoteId }) => ({
  text: typeof text === 'string' ? text : '',
  authorId: authorId || null,
  authorName: authorName || '',
  createdAt: createdAt || null,
  editedAt: null,
  editedById: null,
  editedByName: null,
  removed: false,
  removedAt: null,
  removedById: null,
  removedByName: null,
  sourceNoteId: sourceNoteId || null,
  legacy: false,
});

// An edit never replaces the author — it appends, so a manager rewriting a tech's
// words cannot leave the tech's name standing alone over sentences the tech did
// not write. That is the failure this seam exists to prevent, so it is rendered.
export const describeWorkPerformedAuthor = (entry) => {
  if (!entry) return '';
  const author = entry.legacy || !entry.authorId ? WP_NO_AUTHOR_LABEL : (entry.authorName || 'Unknown');
  if (entry.editedById && entry.editedByName) return `${author} · edited by ${entry.editedByName}`;
  if (entry.editedById) return `${author} · edited`;
  return author;
};
